import React, { useRef, useState } from "react";
import {
    endOfISOWeek,
    format,
    startOfISOWeek,
    subBusinessDays,
} from "date-fns";

import CalendarSectionComponent from "./components/CalendarSection";
import FormSectionComponent from "./components/FormSection";
import HistorySectionComponent from "./components/HistorySection";
import { PageContainer } from "./custom-styles/DailyTimesheetMain.styles";
import PropTypes from "prop-types";
import TimerSectionComponent from "./components/TimerSection";
import TotalsSectionComponent from "./components/TotalsSection";
import { useDailyTimesheet } from "./api/useDailyTimesheet";
import { useQueryClient } from "react-query";

function DailyTimesheetMain({ session }) {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedJob, setSelectedJob] = useState("");
    const [hours, setHours] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const timerRef = useRef(null);
    const queryClient = useQueryClient();

    // Initialize API hooks
    const {
        useAssignments,
        useDailyTotals,
        useRecentEntries,
        useTimesheetSubmitted,
        useDepartmentAssignments,
        updateHours,
    } = useDailyTimesheet();

    // Format date for API calls (YYYY-MM-DD)
    const formatDateForApi = (date) => {
        return format(date, "yyyy-MM-dd");
    };

    // Get week start (Monday) and end (Sunday) for fetching full week's data
    const weekStartDate = startOfISOWeek(selectedDate);
    const weekEndDate = endOfISOWeek(selectedDate);
    const weekStartDateFormatted = formatDateForApi(weekStartDate);
    const weekEndDateFormatted = formatDateForApi(weekEndDate);

    // Get last 2 business days (skipping weekends) as from/to for recent entries
    const today = new Date();
    const twoBizDaysAgo = subBusinessDays(today, 3);
    const todayFormatted = formatDateForApi(today);
    const twoBizDaysAgoFormatted = formatDateForApi(twoBizDaysAgo);

    // Fetch assignments for the week
    const assignmentsData = useAssignments({
        employeeId: session.legacyId,
        fromDate: weekStartDateFormatted,
        weeksCount: 1,
    });

    // Fetch department assignments for the week
    const departmentAssignmentsData = useDepartmentAssignments({
        departmentId: session.currentDepartmentId,
        officeId: session.currentOfficeId,
        agencyId: session.agencyId,
        employeeId: session.legacyId,
        fromDate: weekStartDateFormatted,
        weeksCount: 1,
    });

    // Fetch daily totals for the week
    const dailyTotalsData = useDailyTotals({
        employeeId: session.legacyId,
        fromDate: weekStartDateFormatted,
        toDate: weekEndDateFormatted,
        officeId: session.currentOfficeId,
        agencyId: session.agencyId,
    });

    // Fetch recent time entries (yesterday to today)
    const recentEntriesData = useRecentEntries({
        userId: session.legacyId,
        fromDate: twoBizDaysAgoFormatted,
        toDate: todayFormatted,
    });

    // Check if timesheet is submitted for the current week
    const timesheetSubmittedData = useTimesheetSubmitted({
        employeeId: session.legacyId,
        fromDate: weekStartDateFormatted,
        toDate: weekEndDateFormatted,
    });

    const isTimesheetSubmitted =
        timesheetSubmittedData?.data?.isSubmitted === true;

    const isTimesheetSubmittedLoading =
        timesheetSubmittedData?.isLoading ?? true;

    const handleRefresh = () => {
        queryClient.invalidateQueries("dailyTimesheetAssignments");
        queryClient.invalidateQueries("dailyTimesheetTotals");
        queryClient.invalidateQueries("dailyTimesheetRecentEntries");
        queryClient.invalidateQueries("timesheetSubmitted");
    };

    const isSyncLoading =
        assignmentsData.isFetching ||
        dailyTotalsData.isFetching ||
        recentEntriesData.isFetching ||
        timesheetSubmittedData.isFetching;

    // Handle timer hours callback
    const handleUseTimerHours = (totalHours) => {
        setHours(totalHours);
    };

    // Handle job selection from FormSection
    const handleJobSelect = (jobId) => {
        setSelectedJob(jobId);
    };

    const getSubmitData = () => {
        return {
            employeeId: session.legacyId,
            projectId: selectedJob,
            currentLegacyTitleId: session.currentLegacyTitleId,
            currentOfficeId: session.currentOfficeId,
            departmentId: session.currentDepartmentId,
            date: formatDateForApi(selectedDate),
            hours: Number.parseFloat(hours),
            agencyId: session.agencyId,
        };
    };

    const handleDismissError = () => {
        setShowError(false);
        setErrorMessage("");
    };

    const handleSubmitSuccess = () => {
        setShowSuccess(true);
        setShowError(false); // Hide any previous error

        // Reset form
        setSelectedJob("");
        setHours("");

        // Reset timer
        timerRef.current?.reset();

        // Hide success message after 3 seconds
        setTimeout(() => setShowSuccess(false), 3000);
    };

    const handleSubmitError = (error) => {
        console.error("Error saving timesheet entry:", error);

        // Extract error message from API response
        const message =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to save time entry. Please try again.";

        setErrorMessage(message);
        setShowError(true);
        setShowSuccess(false); // Hide any success message
    };

    const handleSubmit = async () => {
        if (!selectedJob || !hours) {
            return;
        }

        try {
            // Save hours to API (backend handles add/update logic)
            const requestData = getSubmitData();
            await updateHours.mutateAsync(requestData);
            handleSubmitSuccess();
        } catch (error) {
            handleSubmitError(error);
        }
    };

    return (
        <PageContainer>
            <CalendarSectionComponent
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
            />

            <FormSectionComponent
                selectedJob={selectedJob}
                onJobSelect={handleJobSelect}
                assignmentsData={assignmentsData}
                departmentAssignmentsData={departmentAssignmentsData}
                hours={hours}
                setHours={setHours}
                selectedDate={selectedDate}
                handleSubmit={handleSubmit}
                updateHours={updateHours}
                showSuccess={showSuccess}
                showError={showError}
                errorMessage={errorMessage}
                onDismissError={handleDismissError}
                isTimesheetSubmitted={isTimesheetSubmitted}
                isTimesheetSubmittedLoading={isTimesheetSubmittedLoading}
                onRefresh={handleRefresh}
                isLoading={isSyncLoading}
            />

            <TotalsSectionComponent
                dailyTotalsData={dailyTotalsData}
                selectedDate={selectedDate}
                isLoading={dailyTotalsData?.isFetching || false}
            />

            <TimerSectionComponent
                ref={timerRef}
                onUseTimerHours={handleUseTimerHours}
            />

            <HistorySectionComponent
                recentEntriesData={recentEntriesData}
                assignmentsData={assignmentsData}
                departmentAssignmentsData={departmentAssignmentsData}
                onJobSelect={handleJobSelect}
            />
        </PageContainer>
    );
}

DailyTimesheetMain.propTypes = {
    session: PropTypes.shape({
        legacyId: PropTypes.number,
        currentOfficeId: PropTypes.number,
        currentDepartmentId: PropTypes.number,
        agencyId: PropTypes.number,
        currentLegacyTitleId: PropTypes.number,
    }),
};

export default DailyTimesheetMain;
